import {
  Color,
  Combobox,
  DropdownButton,
  Icons,
  NoOptionsPlaceholder
} from '@eggplant/pattern-library'
import { GetRequirementGroupsRequest } from '@eggplant/api-requests'
import {
  HiOutlineSave,
  HiOutlineSearch
} from 'react-icons/hi'
import {
  LinkToImportRequirements,
  useSpaceAccess
} from '@eggplant/routing'
import type {
  ButtonShape,
  DropdownPlacement,
  Options,
  Placement,
  RequirementGroupsResponse
} from '@eggplant/types'

interface Props {
  isFilter?: boolean
  isFilterFixedMinMaxWidth?: boolean
  onChange: (value: string) => void
  placeholder?: string
  requirementGroupsData?: RequirementGroupsResponse[]
  value?: string
  isDisabled?: boolean
  placement?: Placement
  dropdownPlacement?: DropdownPlacement
}

const getOptions = (items: RequirementGroupsResponse[]) =>
  items.map(({
    name,
    id
  }) => ({
    label: name,
    value: id
  }))

const dropDownIcon = () => <Icons.RequirementGroup size='20' />

const SelectRequirementGroup = ({
  isFilter = false,
  isFilterFixedMinMaxWidth = true,
  onChange,
  placeholder = 'Select a requirement group',
  requirementGroupsData,
  value,
  isDisabled = false,
  placement = undefined,
  dropdownPlacement = 'bottom'
// eslint-disable-next-line complexity
}: Props) => {
  const [isAccessible] = useSpaceAccess()

  const renderButton = (options: Options) => {
    const currentOption = options.find(item => item.value === value)

    const label = currentOption?.value
      ? currentOption.label
      : placeholder

    const tooltip = currentOption?.value
      ? `Requirement group: ${currentOption.label}`
      : label

    const buttonText = currentOption?.value
      ? currentOption.label
      : placeholder

    return (buttonProps: ButtonShape) =>
      <DropdownButton
        {...buttonProps}
        Icon={dropDownIcon}
        dropdownPlacement={dropdownPlacement}
        isDisabled={isDisabled}
        isFilter={isFilter}
        isFilterFixedMinMaxWidth={isFilterFixedMinMaxWidth}
        isHighlighted={!!value}
        label={label}
        placement={placement}
        title={tooltip}
      >
        {buttonText}
      </DropdownButton>
  }

  const noDataPlaceholder =
    <NoOptionsPlaceholder
      icon={
        <HiOutlineSearch
          aria-hidden
          size='24px'
        />
      }
      linkTo={
        isAccessible
          ? (
            <LinkToImportRequirements>
              <Color variant='theme-filter-icon'>
                <HiOutlineSave
                  aria-hidden
                  size='16px'
                />
              </Color>
              Import requirements
            </LinkToImportRequirements>
          )
          : undefined
      }
      title='No requirement groups found. Please import requirements to enable requirement groups.'
    />

  const renderComboBox = (requirementsData: RequirementGroupsResponse[]) =>
    <div>
      <Combobox
        dropdownPlacement={dropdownPlacement}
        isDisabled={isDisabled}
        isFilter={isFilter}
        isFilterFixedMinMaxWidth={isFilterFixedMinMaxWidth}
        noOptionsPlaceholder={noDataPlaceholder}
        onChange={onChange}
        options={getOptions(requirementsData)}
        placement={placement}
        renderButton={renderButton(getOptions(requirementsData))}
        value={value}
      />
    </div>

  return requirementGroupsData
    ? renderComboBox(requirementGroupsData)
    : (
      <GetRequirementGroupsRequest shortErrorMessage='Failed to load requirement group'>
        {
          (items: RequirementGroupsResponse[]) =>
            renderComboBox(items)
        }
      </GetRequirementGroupsRequest>
    )
}

export default SelectRequirementGroup
